export type PainelCanalOrigem = "cardapio" | "salao";

export type PainelOrigemFiltro = "todos" | PainelCanalOrigem;

function trimOrNull(value?: string | number | null): string | null {
  if (value == null) return null;
  const t = String(value).trim();
  return t || null;
}

/** Normaliza o rótulo da mesa (prefixa "Mesa " se necessário). */
export function formatIdentificadorMesaLabel(
  identificador?: string | number | null,
): string | null {
  const raw = trimOrNull(identificador);
  if (!raw) return null;
  if (/^mesa\b/i.test(raw)) return raw;
  return `Mesa ${raw}`;
}

function isMesaLikeLabel(value: string): boolean {
  return /^mesa\b/i.test(value.trim());
}

export interface SalaoMesaCliente {
  mesa: string | null;
  cliente: string | null;
}

/**
 * Separa mesa e cliente do FoodFly.
 * `mesaId` = número da mesa; `identificador` = nome do cliente (ou rótulo legado "Mesa …").
 */
export function resolveSalaoMesaCliente(
  mesaId?: string | number | null,
  identificador?: string | null,
): SalaoMesaCliente {
  const mesaFromId = formatIdentificadorMesaLabel(mesaId);
  const idRaw = trimOrNull(identificador);

  if (!idRaw) {
    return { mesa: mesaFromId, cliente: null };
  }

  if (isMesaLikeLabel(idRaw)) {
    return {
      mesa: mesaFromId ?? idRaw,
      cliente: null,
    };
  }

  return {
    mesa: mesaFromId,
    cliente: idRaw,
  };
}

/** Rótulo principal do salão: mesa quando existir, senão nome do cliente. */
export function formatMesaSalaoLabel(
  mesaId?: string | number | null,
  identificador?: string | null,
): string | null {
  const { mesa, cliente } = resolveSalaoMesaCliente(mesaId, identificador);
  return mesa ?? cliente;
}

export interface SalaoProducaoLabels {
  /** Badge principal: Mesa → Comanda → Pedido. */
  badge: string;
  /** Linha/chip secundário (comanda ou pedido). */
  secondary: string | null;
  mesa: string | null;
  cliente: string | null;
  comandaId: string | null;
  /** Rótulo da comanda quando houver (comandaId ou, na mesa, o nº do pedido). */
  comandaLabel: string | null;
  /** Número do pedido do dia (ex.: "Pedido #1"), sempre que houver displayId. */
  pedidoLabel: string | null;
}

/**
 * Labels de produção/painel para salão FoodFly.
 * Prioridade do badge: Mesa → Comanda → Pedido.
 * Com mesa e/ou comanda (incluindo comanda na mesa): NUNCA expor Pedido #.
 */
export function resolveSalaoProducaoLabels(opts: {
  mesaId?: string | number | null;
  comandaId?: string | number | null;
  identificador?: string | null;
  displayId?: number | null;
}): SalaoProducaoLabels {
  const { mesa, cliente } = resolveSalaoMesaCliente(
    opts.mesaId,
    opts.identificador,
  );
  const comandaId = trimOrNull(opts.comandaId);
  const pedidoNum =
    opts.displayId != null && Number.isFinite(opts.displayId)
      ? opts.displayId
      : null;
  const pedidoLabel = pedidoNum != null ? `Pedido #${pedidoNum}` : null;
  const comandaLabel = comandaId ? `Comanda #${comandaId}` : null;

  // Mesa e/ou comanda: não expor ID do pedido — só local + cliente.
  if (mesa) {
    return {
      badge: mesa,
      secondary: comandaLabel,
      mesa,
      cliente,
      comandaId,
      comandaLabel,
      pedidoLabel: null,
    };
  }

  if (comandaId) {
    return {
      badge: `Comanda #${comandaId}`,
      secondary: null,
      mesa,
      cliente,
      comandaId,
      comandaLabel,
      pedidoLabel: null,
    };
  }

  return {
    badge: pedidoLabel ?? "Pedido",
    secondary: null,
    mesa,
    cliente,
    comandaId,
    comandaLabel: null,
    pedidoLabel,
  };
}

export function resolveCanalOrigem(
  origem?: "cardapio" | "salao" | null,
): PainelCanalOrigem {
  return origem === "salao" ? "salao" : "cardapio";
}

/**
 * Pedido de salão via FoodFly (mesa/comanda).
 * Não inclui cardápio, entrega, balcão nem iFood — esses mantêm o visual padrão.
 */
export function isPedidoSalaoFoodfly(opts: {
  origem?: string | null;
  canalOrigem?: string | null;
  tipo?: string | null;
}): boolean {
  if (opts.origem === "salao" || opts.canalOrigem === "salao") return true;
  return opts.tipo === "salao_salgada" || opts.tipo === "salao_doce";
}

/** Dark red dos cards de pedido salão FoodFly. */
export const SALAO_CARD_BG = "#8B0000";

/** Shell completo (painel / sequência). */
export const SALAO_CARD_SHELL_CLASS =
  "overflow-hidden rounded-xl border border-[#5c0000] bg-[#8B0000] text-white shadow-sm";

/** Superfície (borda + fundo + texto) para cards soltos. */
export const SALAO_CARD_SURFACE_CLASS =
  "border-[#5c0000] bg-[#8B0000] text-white";

/** Laranja forte dos cards de pãozinho no painel (mesmo contraste do salão). */
export const PAOZINHO_CARD_BG = "#C2410C";

/** Superfície (borda + fundo + texto) para cards de pãozinho. */
export const PAOZINHO_CARD_SURFACE_CLASS =
  "border-[#9A3412] bg-[#C2410C] text-white";

/** Shell completo para cards de pãozinho. */
export const PAOZINHO_CARD_SHELL_CLASS =
  "overflow-hidden rounded-xl border border-[#9A3412] bg-[#C2410C] text-white shadow-sm";

export function matchesOrigemFiltro(
  filtro: PainelOrigemFiltro,
  canal: PainelCanalOrigem,
): boolean {
  if (filtro === "todos") return true;
  return filtro === canal;
}

const OBS_PLACEHOLDERS = new Set([
  "sem observacao",
  "sem observacoes",
  "nenhuma observacao",
]);

function normalizeObsKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "");
}

/**
 * Deduplica observações (case-insensitive / acentos), quebrando textos já
 * unidos por " | " e ignorando placeholders.
 */
export function dedupeObservacoes(
  ...sources: (string | null | undefined)[]
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of sources) {
    for (const part of (raw ?? "").split(" | ")) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const key = normalizeObsKey(trimmed);
      if (OBS_PLACEHOLDERS.has(key)) continue;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(trimmed);
    }
  }
  return out;
}

export function joinObservacoes(
  ...sources: (string | null | undefined)[]
): string | null {
  const parts = dedupeObservacoes(...sources);
  return parts.length > 0 ? parts.join(" | ") : null;
}
